import { supabase } from "@/integrations/supabase/client";
import { documentWriteError } from "./services/pinnedDocuments";
import type { Database } from "@/integrations/supabase/types";
import { eventLogger } from "@/utils/eventLogger";

type DocumentRow = Database["public"]["Tables"]["documents"]["Row"];
type DocumentInsert = Database["public"]["Tables"]["documents"]["Insert"];
type DocumentUpdate = Database["public"]["Tables"]["documents"]["Update"];

/**
 * Documents Repository
 *
 * Centralizes all Supabase queries for documents domain.
 * Follows the repository pattern: UI → Store → Repository → Supabase
 */

/**
 * List trashed documents for a building
 * Returns documents with is_deleted=true
 */
export async function listTrashedDocuments(buildingId: number): Promise<DocumentRow[]> {
  const { data, error } = await supabase
    .from("documents")
    .select(
      "*, user_profiles!documents_created_by_fk(first_name,last_name), deleted_by_profile:user_profiles!documents_deleted_by_fk(first_name,last_name), document_document_categories(document_categories(*))"
    )
    .eq("building_id", buildingId)
    .eq("is_deleted", true)
    .order("deleted_at", { ascending: false });

  if (error) throw new Error(`Failed to list trashed documents: ${error.message}`);
  return data as DocumentRow[];
}

/**
 * Create a signed URL for downloading a document
 * @param filePath - Storage path to the file
 * @param expiresIn - URL expiration time in seconds (default: 60)
 */
export async function createDownloadUrl(filePath: string, expiresIn = 60): Promise<string> {
  const { data, error } = await supabase.storage
    .from("documents")
    .createSignedUrl(filePath, expiresIn);

  if (error) throw new Error(`Failed to create download URL: ${error.message}`);
  if (!data?.signedUrl) throw new Error("No signed URL returned");

  return data.signedUrl;
}

/**
 * Create a signed URL for showing a document inline. With `width`, Storage
 * serves the image resized to that width (aspect ratio kept) as WebP; without
 * it, the original bytes.
 * @param filePath - Storage path to the file
 * @param expiresIn - URL expiration time in seconds
 */
export async function createPreviewUrl(
  filePath: string,
  { width, expiresIn }: { width?: number; expiresIn: number }
): Promise<string> {
  const { data, error } = await supabase.storage
    .from("documents")
    .createSignedUrl(
      filePath,
      expiresIn,
      width ? { transform: { width, resize: "contain" } } : undefined
    );

  if (error) throw new Error(`Failed to create preview URL: ${error.message}`);
  if (!data?.signedUrl) throw new Error("No signed URL returned");

  return data.signedUrl;
}

/**
 * Move document to trash (soft delete)
 * Updates is_deleted, deleted_at, and deleted_by fields
 */
export async function moveDocumentToTrash(documentId: number, userId: number): Promise<void> {
  const now = new Date().toISOString();

  const { error } = await supabase
    .from("documents")
    .update({
      is_deleted: true,
      deleted_at: now,
      deleted_by: userId,
    })
    .eq("id", documentId);

  if (error) throw documentWriteError(error);
}

/**
 * Move multiple documents to trash in a single operation
 */
export async function moveDocumentsToTrash(documentIds: number[], userId: number): Promise<void> {
  if (documentIds.length === 0) return;

  const now = new Date().toISOString();

  const { error } = await supabase
    .from("documents")
    .update({
      is_deleted: true,
      deleted_at: now,
      deleted_by: userId,
    })
    .in("id", documentIds);

  if (error) throw documentWriteError(error);
}

/**
 * Restore document from trash
 * Clears is_deleted, deleted_at, and deleted_by fields
 */
export async function restoreDocumentFromTrash(documentId: number): Promise<void> {
  const { error } = await supabase
    .from("documents")
    .update({
      is_deleted: false,
      deleted_at: null,
      deleted_by: null,
    })
    .eq("id", documentId);

  if (error) throw new Error(`Failed to restore document: ${error.message}`);
}

/**
 * Restore multiple documents from trash in a single operation
 */
export async function restoreDocumentsFromTrash(documentIds: number[]): Promise<void> {
  if (documentIds.length === 0) return;

  const { error } = await supabase
    .from("documents")
    .update({
      is_deleted: false,
      deleted_at: null,
      deleted_by: null,
    })
    .in("id", documentIds);

  if (error) throw new Error(`Failed to restore documents: ${error.message}`);
}

/**
 * Permanently delete multiple document rows. Storage objects are
 * intentionally kept — documents-bucket objects are never hard-deleted
 * (copies share file_path with their source, and binaries must stay
 * recoverable). Deleted rows are snapshotted into deleted_documents_archive
 * by the trg_archive_deleted_document trigger (franklin-data-pipeline
 * migration 20260721110259_prevent_storage_hard_delete.sql).
 */
export async function permanentDeleteDocuments(documentIds: number[]): Promise<void> {
  if (documentIds.length === 0) return;

  // Delete from database
  const { error: dbError } = await supabase.from("documents").delete().in("id", documentIds);

  if (dbError) throw new Error(`Failed to delete documents from database: ${dbError.message}`);
}

/**
 * Empty trash for a building (permanently delete all trashed documents)
 * Returns count of deleted documents
 */
export async function emptyBuildingTrash(buildingId: number): Promise<number> {
  // Get all trashed documents for this building
  const { data: trashedDocs, error: fetchError } = await supabase
    .from("documents")
    .select("id")
    .eq("building_id", buildingId)
    .eq("is_deleted", true);

  if (fetchError) throw new Error(`Failed to fetch trashed documents: ${fetchError.message}`);

  const count = trashedDocs?.length || 0;

  if (count === 0) {
    return 0;
  }

  const documentIds = trashedDocs.map((doc) => doc.id);

  // Delete all at once
  await permanentDeleteDocuments(documentIds);

  return count;
}

/**
 * Purge documents that have been in trash for more than 30 days
 * Returns count of purged documents
 */
export async function purgeExpiredDocuments(buildingId: number): Promise<number> {
  const threshold = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

  // Find expired trashed documents
  const { data: expiredDocs, error: fetchError } = await supabase
    .from("documents")
    .select("id")
    .eq("building_id", buildingId)
    .eq("is_deleted", true)
    .lt("deleted_at", threshold);

  if (fetchError) throw new Error(`Failed to fetch expired documents: ${fetchError.message}`);

  const count = expiredDocs?.length || 0;
  if (count === 0) return 0;

  const documentIds = expiredDocs.map((d) => d.id);

  // Delete all at once
  await permanentDeleteDocuments(documentIds);

  return count;
}

export const MAX_FILENAME_LENGTH = 255;

export type RenameDocumentError =
  | { kind: "empty" }
  | { kind: "tooLong"; max: number }
  | { kind: "invalidChars" }
  | { kind: "extensionChanged"; expected: string }
  | { kind: "collision"; existingFilename: string }
  | { kind: "notFound" }
  | { kind: "unknown"; message: string };

export class RenameDocumentValidationError extends Error {
  readonly cause: RenameDocumentError;
  constructor(cause: RenameDocumentError) {
    super(cause.kind);
    this.name = "RenameDocumentValidationError";
    this.cause = cause;
  }
}

function splitFilename(name: string): { base: string; ext: string } {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) {
    // No extension, or hidden file (".env"), or trailing dot — treat as no extension.
    return { base: name, ext: "" };
  }
  return { base: name.slice(0, lastDot), ext: name.slice(lastDot) };
}

/**
 * Rename a document, preserving its file extension. Validates length, characters,
 * extension preservation, and uniqueness within (building_id, folder_id) before
 * updating. RLS gates the actual write — callers should still gate the UI.
 *
 * Throws RenameDocumentValidationError for any rule violation. The caller can
 * inspect `error.cause.kind` to render a localized message.
 */
export async function renameDocument(documentId: number, newName: string): Promise<void> {
  const trimmed = newName.trim();
  if (trimmed.length === 0) {
    throw new RenameDocumentValidationError({ kind: "empty" });
  }
  if (trimmed.length > MAX_FILENAME_LENGTH) {
    throw new RenameDocumentValidationError({ kind: "tooLong", max: MAX_FILENAME_LENGTH });
  }
  // Block path separators, Windows-reserved characters, and control characters
  // that would corrupt download Content-Disposition headers or shell out
  // unexpectedly.
  const hasControlCharacter = Array.from(trimmed).some(
    (character) => character.charCodeAt(0) <= 31
  );
  if (hasControlCharacter || /[/\\<>:"|?*]/.test(trimmed)) {
    throw new RenameDocumentValidationError({ kind: "invalidChars" });
  }

  const { data: current, error: fetchError } = await supabase
    .from("documents")
    .select("id, filename, building_id, folder_id, project_id, org_id")
    .eq("id", documentId)
    .maybeSingle();
  if (fetchError) {
    throw new RenameDocumentValidationError({ kind: "unknown", message: fetchError.message });
  }
  if (!current) {
    throw new RenameDocumentValidationError({ kind: "notFound" });
  }

  const currentExt = splitFilename(current.filename).ext;
  const newExt = splitFilename(trimmed).ext;
  if (currentExt.toLowerCase() !== newExt.toLowerCase()) {
    throw new RenameDocumentValidationError({ kind: "extensionChanged", expected: currentExt });
  }
  if (trimmed === current.filename) {
    return;
  }

  // Uniqueness within (building_id, folder_id) for non-deleted rows.
  // Mirrors the duplicate-detection key used by the upload pipeline.
  let collisionQuery = supabase
    .from("documents")
    .select("id, filename")
    .eq("building_id", current.building_id)
    .eq("filename", trimmed)
    .eq("is_deleted", false)
    .neq("id", documentId);
  if (current.folder_id === null) {
    collisionQuery = collisionQuery.is("folder_id", null);
  } else {
    collisionQuery = collisionQuery.eq("folder_id", current.folder_id);
  }
  const { data: collisions, error: collisionError } = await collisionQuery.limit(1);
  if (collisionError) {
    throw new RenameDocumentValidationError({ kind: "unknown", message: collisionError.message });
  }
  if (collisions && collisions.length > 0) {
    throw new RenameDocumentValidationError({
      kind: "collision",
      existingFilename: collisions[0].filename,
    });
  }

  const { error } = await supabase
    .from("documents")
    .update({ filename: trimmed } satisfies DocumentUpdate)
    .eq("id", documentId);
  if (error) {
    throw new RenameDocumentValidationError({ kind: "unknown", message: error.message });
  }

  await eventLogger.log({
    event_type: "document.renamed",
    entity_type: "document",
    entity_id: String(documentId),
    org_id: current.org_id,
    field_name: "filename",
    previous_value: current.filename,
    new_value: trimmed,
    metadata: {
      filename: trimmed,
      previousFilename: current.filename,
      buildingId: current.building_id,
      projectId: current.project_id,
    },
  });
}

/**
 * Find all versions of a document based on filename pattern
 * Returns document IDs and file paths
 */
export async function findDocumentVersions(
  buildingId: number,
  baseName: string,
  extension: string,
  isDeleted: boolean
): Promise<Array<{ id: number; filename: string; file_path: string }>> {
  const { data, error } = await supabase
    .from("documents")
    .select("id, filename, file_path")
    .eq("building_id", buildingId)
    .eq("is_deleted", isDeleted);

  if (error) throw new Error(`Failed to find document versions: ${error.message}`);

  // Filter documents that match version patterns
  const versions =
    data?.filter((doc) => {
      const docParts = doc.filename.split(".");
      const docExt = docParts.length > 1 ? `.${docParts.pop()}` : "";
      const docNameWithoutExt = docParts.join(".");

      const docBaseName = docNameWithoutExt
        .replace(/[-_\s]v\d+$/i, "")
        .replace(/[-_\s]\d+$/i, "")
        .replace(/\s+\(\d+\)$/i, "");

      // Check if this document belongs to the same version group
      return docBaseName + docExt === baseName + extension;
    }) || [];

  return versions;
}
